import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { Layout, ProtectedRoute } from '../shared/layout';

// Route constants
import {
  ROUTES,
  AUTH_ROUTES,
  REPORTS_ROUTES,
  SETTINGS_ROUTES,
  JUNIPER_ROUTES,
  SUPPORT_ROUTES,
  PUBLIC_ROUTES,
} from '../shared/constants';

// Feature imports
import {
  LoginPage,
  AuthCallbackPage,
  ClioCallbackPage,
  QboCallbackPage,
} from '../features/auth/pages';
import { ReportsPage } from '../features/reports/pages';
import { ARDashboardPage, ARHistoryPage } from '../features/reports/pages/arReportPage';
import {
  TrustDashboardPage,
  TrustHistoryPage,
} from '../features/reports/pages/trustReconciliationPage';
import {
  MatterLedgerDashboardPage,
  MatterLedgerHistoryPage,
} from '../features/reports/pages/matterLedgerReportPage';
import {
  MatterStatementDashboardPage,
  MatterStatementHistoryPage,
} from '../features/reports/pages/matterStatementReportPage';
import { JuniperEmailDashboardPage } from '../features/reports/pages/juniperEmailReportPage';
import {
  SettingsPage,
  ReportingConfigPage,
  DataReplicationConfigurationPage,
  ClioIntegrationPage,
} from '../features/settings/pages';
import {
  ClioWebhookLifecycleManagerPage,
  WebhookActivityPage,
  QboWebhookSettingsPage,
  QboWebhookActivityPage,
} from '../features/webhooks/pages';
import {
  JuniperConfigPage,
  JuniperTransactionsPage,
  PdfViewerPage,
  DocumentViewerPage,
} from '../features/juniper/pages';
import { ExternalArchivalPage } from '../features/archival/pages';
import { TemplateManagerPage } from '../features/templates/pages';
import { FeedbackPage } from '../features/support/pages/feedbackPage/FeedbackPage';
import { TrustRequestCreationPage } from '../features/trust-requests/pages';
import {
  ClioMattersReplicationPage,
  ClioContactsReplicationPage,
  ClioBillsReplicationPage,
  ClioBankAccountsReplicationPage,
  ClioBankTransactionsReplicationPage,
  ClioActivitiesReplicationPage,
  ClioActivityDescriptionsReplicationPage,
  ClioExpenseCategoriesReplicationPage,
  ClioTasksReplicationPage,
  ClioUsersReplicationPage,
  ClioLineItemsReplicationPage,
  ClioAllocationsReplicationPage,
  ClioUtbmsCodesReplicationPage,
  ClioUtbmsSetsReplicationPage,
} from '../features/data-replication/pages';

// Shared pages
import { NotFoundPage, PrivacyPolicyPage, TermsOfServicePage } from '../shared/pages';

const router = createBrowserRouter([
  // ============================================
  // Protected Routes (Main App)
  // ============================================
  {
    path: ROUTES.HOME,
    element: (
      <ProtectedRoute>
        <Layout />
      </ProtectedRoute>
    ),
    children: [
      // Default redirect
      { index: true, element: <Navigate to={JUNIPER_ROUTES.TRANSACTIONS} replace /> },

      // ============================================
      // Reports Routes
      // ============================================
      { path: REPORTS_ROUTES.BASE.replace('/', ''), element: <ReportsPage /> },
      {
        path: REPORTS_ROUTES.AR.BASE.replace('/', ''),
        element: <Navigate to={REPORTS_ROUTES.AR.DASHBOARD} replace />,
      },
      { path: REPORTS_ROUTES.AR.DASHBOARD.replace('/', ''), element: <ARDashboardPage /> },
      { path: REPORTS_ROUTES.AR.HISTORY.replace('/', ''), element: <ARHistoryPage /> },
      {
        path: REPORTS_ROUTES.TRUST.BASE.replace('/', ''),
        element: <Navigate to={REPORTS_ROUTES.TRUST.DASHBOARD} replace />,
      },
      { path: REPORTS_ROUTES.TRUST.DASHBOARD.replace('/', ''), element: <TrustDashboardPage /> },
      { path: REPORTS_ROUTES.TRUST.HISTORY.replace('/', ''), element: <TrustHistoryPage /> },
      {
        path: REPORTS_ROUTES.MATTER_LEDGER.BASE.replace('/', ''),
        element: <Navigate to={REPORTS_ROUTES.MATTER_LEDGER.DASHBOARD} replace />,
      },
      {
        path: REPORTS_ROUTES.MATTER_LEDGER.DASHBOARD.replace('/', ''),
        element: <MatterLedgerDashboardPage />,
      },
      {
        path: REPORTS_ROUTES.MATTER_LEDGER.HISTORY.replace('/', ''),
        element: <MatterLedgerHistoryPage />,
      },
      {
        path: REPORTS_ROUTES.MATTER_STATEMENT.BASE.replace('/', ''),
        element: <Navigate to={REPORTS_ROUTES.MATTER_STATEMENT.DASHBOARD} replace />,
      },
      {
        path: REPORTS_ROUTES.MATTER_STATEMENT.DASHBOARD.replace('/', ''),
        element: <MatterStatementDashboardPage />,
      },
      {
        path: REPORTS_ROUTES.MATTER_STATEMENT.HISTORY.replace('/', ''),
        element: <MatterStatementHistoryPage />,
      },
      {
        path: REPORTS_ROUTES.JUNIPER_EMAIL.BASE.replace('/', ''),
        element: <JuniperEmailDashboardPage />,
      },

      // ============================================
      // Settings Routes
      // ============================================
      { path: SETTINGS_ROUTES.BASE.replace('/', ''), element: <SettingsPage /> },
      { path: SETTINGS_ROUTES.REPORTING_CONFIG.replace('/', ''), element: <ReportingConfigPage /> },
      {
        path: SETTINGS_ROUTES.DATA_REPLICATION_CONFIGURATION.replace('/', ''),
        element: <DataReplicationConfigurationPage />,
      },
      { path: SETTINGS_ROUTES.ARCHIVAL.replace('/', ''), element: <ExternalArchivalPage /> },
      { path: SETTINGS_ROUTES.JUNIPER_CONFIG.replace('/', ''), element: <JuniperConfigPage /> },
      { path: SETTINGS_ROUTES.TEMPLATE_MANAGER.replace('/', ''), element: <TemplateManagerPage /> },
      { path: SETTINGS_ROUTES.CLIO_INTEGRATION.replace('/', ''), element: <ClioIntegrationPage /> },
      {
        path: SETTINGS_ROUTES.WEBHOOKS.replace('/', ''),
        element: <ClioWebhookLifecycleManagerPage />,
      },
      { path: SETTINGS_ROUTES.WEBHOOK_ACTIVITY.replace('/', ''), element: <WebhookActivityPage /> },
      { path: SETTINGS_ROUTES.QBO_WEBHOOKS.replace('/', ''), element: <QboWebhookSettingsPage /> },
      {
        path: SETTINGS_ROUTES.QBO_WEBHOOK_ACTIVITY.replace('/', ''),
        element: <QboWebhookActivityPage />,
      },
      {
        path: SETTINGS_ROUTES.DATA_REPLICATION_MATTERS.replace('/', ''),
        element: <ClioMattersReplicationPage />,
      },
      {
        path: SETTINGS_ROUTES.DATA_REPLICATION_CONTACTS.replace('/', ''),
        element: <ClioContactsReplicationPage />,
      },
      {
        path: SETTINGS_ROUTES.DATA_REPLICATION_BILLS.replace('/', ''),
        element: <ClioBillsReplicationPage />,
      },
      {
        path: SETTINGS_ROUTES.DATA_REPLICATION_BANK_ACCOUNTS.replace('/', ''),
        element: <ClioBankAccountsReplicationPage />,
      },
      {
        path: SETTINGS_ROUTES.DATA_REPLICATION_BANK_TRANSACTIONS.replace('/', ''),
        element: <ClioBankTransactionsReplicationPage />,
      },
      {
        path: SETTINGS_ROUTES.DATA_REPLICATION_ACTIVITIES.replace('/', ''),
        element: <ClioActivitiesReplicationPage />,
      },
      {
        path: SETTINGS_ROUTES.DATA_REPLICATION_ACTIVITY_DESCRIPTIONS.replace('/', ''),
        element: <ClioActivityDescriptionsReplicationPage />,
      },
      {
        path: SETTINGS_ROUTES.DATA_REPLICATION_EXPENSE_CATEGORIES.replace('/', ''),
        element: <ClioExpenseCategoriesReplicationPage />,
      },
      {
        path: SETTINGS_ROUTES.DATA_REPLICATION_TASKS.replace('/', ''),
        element: <ClioTasksReplicationPage />,
      },
      {
        path: SETTINGS_ROUTES.DATA_REPLICATION_USERS.replace('/', ''),
        element: <ClioUsersReplicationPage />,
      },
      {
        path: SETTINGS_ROUTES.DATA_REPLICATION_LINE_ITEMS.replace('/', ''),
        element: <ClioLineItemsReplicationPage />,
      },
      {
        path: SETTINGS_ROUTES.DATA_REPLICATION_ALLOCATIONS.replace('/', ''),
        element: <ClioAllocationsReplicationPage />,
      },
      {
        path: SETTINGS_ROUTES.DATA_REPLICATION_UTBMS_CODES.replace('/', ''),
        element: <ClioUtbmsCodesReplicationPage />,
      },
      {
        path: SETTINGS_ROUTES.DATA_REPLICATION_UTBMS_SETS.replace('/', ''),
        element: <ClioUtbmsSetsReplicationPage />,
      },

      // ============================================
      // Juniper Routes
      // ============================================
      { path: JUNIPER_ROUTES.TRANSACTIONS.replace('/', ''), element: <JuniperTransactionsPage /> },

      // ============================================
      // Trust Request Routes
      // ============================================
      { path: 'trust-requests/create', element: <TrustRequestCreationPage /> },

      // ============================================
      // Support Routes
      // ============================================
      { path: SUPPORT_ROUTES.FEEDBACK.replace('/', ''), element: <FeedbackPage /> },
    ],
  },

  // ============================================
  // Special Protected Routes (Viewers)
  // ============================================
  {
    path: JUNIPER_ROUTES.PDF_VIEWER,
    element: (
      <ProtectedRoute>
        <PdfViewerPage />
      </ProtectedRoute>
    ),
  },
  {
    path: JUNIPER_ROUTES.DOCUMENT_VIEWER,
    element: (
      <ProtectedRoute>
        <DocumentViewerPage />
      </ProtectedRoute>
    ),
  },

  // ============================================
  // Public Routes (Auth)
  // ============================================
  {
    path: ROUTES.LOGIN,
    element: <LoginPage />,
  },
  {
    path: AUTH_ROUTES.CALLBACK,
    element: <AuthCallbackPage />,
  },
  {
    path: AUTH_ROUTES.ERROR,
    element: <AuthCallbackPage />,
  },
  {
    path: AUTH_ROUTES.CLIO_REDIRECT,
    element: <ClioCallbackPage />,
  },
  {
    path: AUTH_ROUTES.QBO_REDIRECT,
    element: <QboCallbackPage />,
  },

  // ============================================
  // Public Routes (Legal)
  // ============================================
  {
    path: PUBLIC_ROUTES.PRIVACY_POLICY,
    element: <PrivacyPolicyPage />,
  },
  {
    path: PUBLIC_ROUTES.TERMS_OF_SERVICE,
    element: <TermsOfServicePage />,
  },

  // ============================================
  // Fallback Route
  // ============================================
  {
    path: ROUTES.NOT_FOUND,
    element: <NotFoundPage />,
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
